import { db } from "../firebase-config";
import { collection, query, doc, updateDoc, deleteDoc, where, getDocs, addDoc, onSnapshot } from "firebase/firestore";

// Real-time listener for client-specific events
export const clientEventSnapshotListener = (organizationId, customerId, onEventUpdate) => {
  const collectionPath = `organizations/${organizationId}/events`;
  const eventQuery = query(
    collection(db, collectionPath),
    where("client_id", "==", customerId)
  );

  const unsubscribe = onSnapshot(eventQuery, (querySnapshot) => {
      const eventItems = [];
      querySnapshot.forEach((doc) => {
        eventItems.push({ id: doc.id, ...doc.data() });
      });
      onEventUpdate(eventItems);
  })
  return () => unsubscribe()
}

// Add a new event
export const addEvent = async (organization, event, isPrivate = false, includedUsers = []) => {
  try {
    event.is_private = isPrivate;
    if (isPrivate) {
      event.included_users = includedUsers;
    }
    await addDoc(collection(db, "organizations", organization, "events"), event);
    return { success: true, error: null };
  } catch (error) {
    console.error("Error adding event:", error);
    return { success: false, error: error.message || 'Failed to add event' };
  }
};

// Get events based on user role
export const getEvents = async (organization, userId, isAdministrator) => {
  try {
    const eventsRef = collection(db, 'organizations', organization, "events");
    const q = isAdministrator
      ? query(eventsRef)
      : query(eventsRef, where("assigned_employees", "array-contains", userId));

    const querySnapshot = await getDocs(q);
    const eventDocs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    return { success: true, data: eventDocs, error: null };
  } catch (error) {
    console.error("Error fetching events:", error);
    return { success: false, data: null, error: error.message || 'Failed to fetch events' };
  }
};

// Real-time listener for public and private events
export const eventSnapshotListener = (organization, userId, onEventsUpdate) => {
  const publicEventsQuery = query(
      collection(db, 'organizations', organization, "events"),
      where("is_private", "==", false)
  );
  
  const privateEventsQuery = query(
      collection(db, 'organizations', organization, "events"),
      where("is_private", "==", true),
      where("included_users", "array-contains", userId)
  );

  const unsubscribePublic = onSnapshot(publicEventsQuery, (querySnapshot) => {
      const publicEvents = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const unsubscribePrivate = onSnapshot(privateEventsQuery, (privateSnapshot) => {
          const privateEvents = privateSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          onEventsUpdate([...publicEvents, ...privateEvents]);
      });

      return () => {
          unsubscribePrivate();
      };
  }, 
  (error) => {
      onEventsUpdate(null);
  });

  return () => {
      unsubscribePublic();
  };
};

// Delete an event
export const deleteEvent = async (organization, eventId) => {
  try {
    await deleteDoc(doc(db, "organizations", organization, "events", eventId));
    return { success: true, error: null };
  } catch (error) {
    console.error("Error deleting event:", error);
    return { success: false, error: error.message || 'Failed to delete event' };
  }
};

// Update an event
export const updateEvent = async (organization, eventId, eventData) => {
  try {
    const eventRef = doc(db, "organizations", organization, "events", eventId);
    await updateDoc(eventRef, eventData);
    return { success: true, error: null };
  } catch (error) {
    console.error("Error updating event:", error);
    return { success: false, error: error.message || 'Failed to update event' };
  }
};
